import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { useTranslation } from 'react-i18next';
import BetaBanner from './BetaBanner';
import ChangePasswordModal from './ChangePasswordModal';


const BACKEND_URL = process.env.REACT_APP_API_URL;

interface PlayerLeague {
  id: number;
  name: string;
  venue: string;
  city: string;
  country: string;
  teamFormat: string;
  startDate: string;
  endDate: string;
  teamName: string;
  registrationStatus: string;
  paymentComplete: boolean;
}

interface SALeague {
  id: number;
  name: string;
  venue: string;
  city: string | null;
  country: string | null;
  start_date: string;
  end_date: string;
  bookie_name: string;
}

const PlayerDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { canAccessPremiumFeatures } = useSubscription();

  const [playerLeagues, setPlayerLeagues] = useState<PlayerLeague[]>([]);
  const [loadingLeagues, setLoadingLeagues] = useState(true);
  const [saLeagues, setSaLeagues] = useState<SALeague[]>([]);
  const [loadingSALeagues, setLoadingSALeagues] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isProcessingSubscription, setIsProcessingSubscription] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  
  // Support form state
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportReason, setSupportReason] = useState('');
  const [supportCategory, setSupportCategory] = useState('');
  const [supportArea, setSupportArea] = useState('');
  const [supportDetails, setSupportDetails] = useState('');
  const [submittingSupport, setSubmittingSupport] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (canAccessPremiumFeatures && user) {
      fetchPlayerLeagues();
      fetchSALeagues();
    } else {
      setLoadingLeagues(false);
      setLoadingSALeagues(false);
    }
  }, [canAccessPremiumFeatures, user]);

  const fetchPlayerLeagues = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/player/leagues`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const leagues = await response.json();
        setPlayerLeagues(leagues);
      }
    } catch (error) {
      console.error('Failed to fetch player leagues:', error);
    } finally {
      setLoadingLeagues(false);
    }
  };

  const fetchSALeagues = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/player/side-action-leagues`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSaLeagues(data);
      }
    } catch (error) {
      console.error('Failed to fetch SA leagues:', error);
    } finally {
      setLoadingSALeagues(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/'); // Redirect to welcome page after logout
  };

  const handleSubscribe = async () => {
    if (!user) return;

    setIsProcessingSubscription(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          playerId: user.id,
          playerEmail: user.email,
          playerName: user.name
        })
      });

      const data = await response.json();

      if (data.success && data.stripeUrl) {
        // Redirect to Stripe checkout
        window.location.href = data.stripeUrl;
      } else {
        console.error('Subscription error:', data.error);
        alert(data.error || t('messages.subscriptionFailed'));
      }
    } catch (error) {
      console.error('Failed to process subscription:', error);
      alert(t('messages.subscriptionFailed'));
    } finally {
      setIsProcessingSubscription(false);
      setShowSubscriptionModal(false);
    }
  };

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!supportReason || !supportDetails.trim()) {
      return;
    }
    
    setSubmittingSupport(true);
    
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/support/submit/player`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          reason: supportReason,
          category: supportCategory || null,
          subcategory: supportArea || null,
          questions: supportDetails
        })
      });
      
      if (response.ok) {
        setSuccessMessage(t('player.support.submitSuccess'));
        setShowSupportModal(false);
        setSupportReason('');
        setSupportCategory('');
        setSupportArea('');
        setSupportDetails('');
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        const errorData = await response.json();
        alert(errorData.error || t('player.support.submitError'));
      }
    } catch (error) {
      console.error('Support submission error:', error);
      alert(t('player.support.submitError'));
    } finally {
      setSubmittingSupport(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header with User Info and Logout */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center py-4 sm:py-6 gap-3">
            {/* User Info */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-base sm:text-lg">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center space-x-2 flex-wrap">
                  <h1 className="text-lg sm:text-2xl font-bold text-white">
                    {t('player.welcome', { name: user.name })}
                  </h1>
                  {/* Subscription Status Badge */}
                  {canAccessPremiumFeatures ? (
                    <div className="flex items-center space-x-1 bg-gradient-to-r from-yellow-400 to-yellow-600 px-2 sm:px-3 py-1 rounded-full">
                      <span className="text-yellow-900 text-xs sm:text-sm font-semibold"><span className="hidden sm:inline">👑 </span>{t('player.premiumMember')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1 bg-gray-600 px-2 sm:px-3 py-1 rounded-full">
                      <span className="text-gray-200 text-xs sm:text-sm font-semibold"><span className="hidden sm:inline">🔒 </span>{t('player.freeAccount')}</span>
                    </div>
                  )}
                </div>
                <p className="text-blue-200 text-xs sm:text-sm">
                  {user.city && user.country ? `${user.city}, ${user.country}` : t('player.playerDashboard')}
                </p>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="group flex items-center space-x-2 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 px-3 py-2 sm:px-6 sm:py-3 rounded-lg transition-all duration-200 transform hover:scale-105"
            >
              <span className="hidden sm:inline text-white font-semibold">{t('common.logout')}</span>
              <svg
                className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform duration-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Floating Support Button */}
      <button
        className="fixed bottom-5 right-6 z-50 flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-md border border-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg transition duration-200"
        onClick={() => setShowSupportModal(true)}
        title="Contact Support 24/7"
      >
        <span className="text-base">🎧</span>
        <span>Contact Support 24/7</span>
      </button>

      {/* Support Modal */}
      {showSupportModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowSupportModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
            style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Support Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-4xl text-green-300 font-bold">💬</span>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-white text-center mb-6">{t('player.support.title')}</h2>
            
            <form onSubmit={handleSupportSubmit} className="space-y-4">
              {/* Reason for Inquiry */}
              <div>
                <label className="block text-white font-semibold mb-2">
                  {t('player.support.reasonLabel')}
                </label>
                <select
                  value={supportReason}
                  onChange={(e) => {
                    setSupportReason(e.target.value);
                    setSupportCategory(''); // Reset category when reason changes
                  }}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="" className="bg-gray-800">{t('player.support.selectReason')}</option>
                  <option value="Joining leagues" className="bg-gray-800">{t('player.support.reasons.joiningLeagues')}</option>
                  <option value="League Dashboard help" className="bg-gray-800">{t('player.support.reasons.leagueDashboardHelp')}</option>
                  <option value="Creating/registering a team" className="bg-gray-800">{t('player.support.reasons.creatingTeam')}</option>
                  <option value="Something isn't working as expected" className="bg-gray-800">{t('player.support.reasons.notWorkingExpected')}</option>
                  <option value="Need help figuring something out" className="bg-gray-800">{t('player.support.reasons.needHelp')}</option>
                </select>
              </div>
              
              {/* Category - Only show for specific reasons */}
              {(supportReason === 'Joining leagues' || supportReason === 'League Dashboard help' || supportReason === 'Creating/registering a team') && (
                <div>
                  <label className="block text-white font-semibold mb-2">
                    {t('player.support.categoryLabel')}
                  </label>
                  <select
                    value={supportCategory}
                    onChange={(e) => setSupportCategory(e.target.value)}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  >
                    <option value="" className="bg-gray-800">{t('player.support.selectCategory')}</option>
                    
                    {supportReason === 'Joining leagues' && (
                      <>
                        <option value="Need Help with league navigation" className="bg-gray-800">{t('player.support.categories.joiningLeagues.leagueNavigation')}</option>
                        <option value="Need help finding a specific league" className="bg-gray-800">{t('player.support.categories.joiningLeagues.findingLeague')}</option>
                        <option value="Need help finding an eligible team for a league" className="bg-gray-800">{t('player.support.categories.joiningLeagues.findingTeam')}</option>
                        <option value="I am encountering an error when navigating the leagues" className="bg-gray-800">{t('player.support.categories.joiningLeagues.errorNavigating')}</option>
                        <option value="I am encountering an error when clicking a league" className="bg-gray-800">{t('player.support.categories.joiningLeagues.errorClicking')}</option>
                        <option value="I am encountering an error when registering for a league" className="bg-gray-800">{t('player.support.categories.joiningLeagues.errorRegistering')}</option>
                      </>
                    )}
                    
                    {supportReason === 'League Dashboard help' && (
                      <>
                        <option value="Need help understanding my schedule or there is an error" className="bg-gray-800">{t('player.support.categories.leagueDashboard.scheduleHelp')}</option>
                        <option value="Need help with my matches or there is an error" className="bg-gray-800">{t('player.support.categories.leagueDashboard.matchesHelp')}</option>
                        <option value="Need help understanding the match scores or there is an error" className="bg-gray-800">{t('player.support.categories.leagueDashboard.scoresHelp')}</option>
                        <option value="Need help understanding the leaderboards or there is an error" className="bg-gray-800">{t('player.support.categories.leagueDashboard.leaderboardsHelp')}</option>
                        <option value="My points or team name are not showing or are wrong" className="bg-gray-800">{t('player.support.categories.leagueDashboard.pointsTeamName')}</option>
                      </>
                    )}
                    
                    {supportReason === 'Creating/registering a team' && (
                      <>
                        <option value="Need help creating a team" className="bg-gray-800">{t('player.support.categories.creatingTeam.creatingHelp')}</option>
                        <option value="Need help joining a team" className="bg-gray-800">{t('player.support.categories.creatingTeam.joiningHelp')}</option>
                        <option value="I am having eligibility problems with a team or a team is not showing up" className="bg-gray-800">{t('player.support.categories.creatingTeam.eligibilityProblems')}</option>
                        <option value="I have a general question regarding the teams functionality" className="bg-gray-800">{t('player.support.categories.creatingTeam.generalQuestion')}</option>
                      </>
                    )}
                  </select>
                </div>
              )}
              
              {/* Area (HELP/ERROR/BUG) - subcategory */}
              <div>
                <label className="block text-white font-semibold mb-2">
                  {t('player.support.areaLabel')}
                </label>
                <select
                  value={supportArea}
                  onChange={(e) => setSupportArea(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="" className="bg-gray-800">{t('player.support.selectArea')}</option>
                  <option value="HELP" className="bg-gray-800">{t('player.support.areas.help')}</option>
                  <option value="ERROR" className="bg-gray-800">{t('player.support.areas.error')}</option>
                  <option value="BUG" className="bg-gray-800">{t('player.support.areas.bug')}</option>
                </select>
              </div>
              
              {/* Details Text Area */}
              <div>
                <label className="block text-white font-semibold mb-2">
                  {t('player.support.detailsLabel')}
                </label>
                <textarea
                  value={supportDetails}
                  onChange={(e) => setSupportDetails(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500 min-h-32"
                  placeholder={t('player.support.detailsPlaceholder')}
                  required
                />
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowSupportModal(false)}
                  className="flex-1 bg-gray-600 text-white py-2 rounded-lg font-bold hover:bg-gray-700 transition"
                >
                  {t('common.close')}
                </button>
                <button
                  type="submit"
                  disabled={submittingSupport || !supportReason || !supportArea || !supportDetails.trim()}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg font-bold hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingSupport ? t('common.Processing') : t('common.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-8">
          {/* Beta Banner */}
          <BetaBanner />
          
          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-500/20 border border-green-400 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <div className="text-green-400 text-xl">✅</div>
                <p className="text-green-200 font-medium">{successMessage}</p>
              </div>
            </div>
          )}
          <div className="bg-white/10 text-center backdrop-blur-lg rounded-xl p-2 border border-white/20">
            <h2 className="text-3xl font-bold text-white">🎳 {t('player.playerDashboard')}</h2>
          </div>
          {/* Welcome Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20">
            <div>

              {!canAccessPremiumFeatures ? (
                <div className="bg-orange-500/20 border border-orange-400 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-2xl">🔒</span>
                    <div className="text-center">
                      <p className="text-orange-200 font-semibold">{t('player.upgradeToPremium')}</p>
                      <p className="text-orange-300 text-sm">{t('player.subscribeToAccess')}</p>
                      <button
                        onClick={() => setShowSubscriptionModal(true)}
                        className="mt-3 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-yellow-900 font-bold py-2 px-6 rounded-lg transition duration-200"
                      >
                        {t('player.upgradeNow')}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="px-2 mb-4 flex items-center ">
                    <h3 className="text-2xl font-bold text-white">{t('player.myLeagues')}</h3>
                    <button
                      onClick={() => navigate('/player/calendar')}
                      className="bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 text-white font-semibold py-2 px-6 ml-4 rounded-lg transition duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <span>🗓️</span>
                      <span>{t('player.viewCalendar')}</span>
                    </button>
                  </div>

                  {loadingLeagues ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
                      <span className="ml-2 text-green-300">{t('player.loadingLeagues')}</span>
                    </div>
                  ) : playerLeagues.length === 0 ? (
                    <div className="text-center py-6">
                      <div className="text-4xl mb-3">🎳</div>
                      <p className="text-green-300 mb-2">{t('player.noLeaguesYet')}</p>
                      <p className="text-green-400 text-sm mb-4">{t('player.readyToJoin')}</p>
                      <button
                        onClick={() => navigate('/player/leagues/browse')}
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
                      >
                        {t('player.browseLeaguesButton')}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Scrollable grid for league cards */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-y-auto custom-scrollbar"
                        style={{
                          maxHeight: '32rem', // Adjust for exactly 2 rows (depends on card height)
                          scrollbarWidth: 'thin',
                          scrollbarColor: 'rgba(255,255,255,0.2) transparent'
                        }}>
                        {playerLeagues.map((league) => (
                          <div
                            key={league.id}
                            className="bg-white/10 hover:bg-white/15 rounded-xl border border-white/10 shadow-lg p-5 flex flex-col justify-between cursor-pointer transition duration-200"
                            onClick={() => navigate(`/player/leagues/${league.id}`)}
                          >
                            <div>
                              <div className="flex items-center space-x-2 mb-3">
                                <h4 className="text-white font-semibold text-lg truncate">{league.name}</h4>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${league.registrationStatus === 'active' || league.registrationStatus === 'completed'
                                  ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                  : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                                  }`}>
                                  {league.registrationStatus}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${league.paymentComplete
                                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                                  : 'bg-red-500/20 text-red-300 border border-red-500/30'
                                  }`}>
                                  {league.paymentComplete ? t('player.paid') : t('player.paymentPending')}
                                </span>
                              </div>
                              <div className="space-y-1 text-sm text-blue-200">
                                <div>
                                  <span className="font-semibold text-white">{t('league.team')}:</span>
                                  <span className="ml-1">{league.teamName}</span>
                                </div>
                                <div>
                                  <span className="font-semibold text-white">{t('league.venue')}:</span>
                                  <span className="ml-1">{league.venue}, {league.city}</span>
                                </div>
                                <div>
                                  <span className="font-semibold text-white">{t('league.format')}:</span>
                                  <span className="ml-1">{league.teamFormat}</span>
                                </div>
                                <div>
                                  <span className="font-semibold text-white">{t('league.duration')}:</span>
                                  <span className="ml-1">{new Date(league.startDate).toLocaleDateString()} - {new Date(league.endDate).toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Side Action Leagues Section */}
          {canAccessPremiumFeatures && (loadingSALeagues || saLeagues.length > 0) && (
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-orange-400/30">
              <div className="px-2 mb-4 flex items-center gap-3">
               
                <h3 className="text-2xl font-bold text-white">My Side Action Leagues</h3>
              </div>
              {loadingSALeagues ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-400"></div>
                  <span className="ml-2 text-orange-300">Loading...</span>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-y-auto custom-scrollbar"
                  style={{ maxHeight: '24rem', scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,160,50,0.3) transparent' }}>
                  {saLeagues.map(league => (
                    <div
                      key={league.id}
                      className="bg-orange-500/10 hover:bg-orange-500/20 rounded-xl border border-orange-400/20 shadow-lg p-5 flex flex-col justify-between cursor-pointer transition duration-200"
                      onClick={() => navigate(`/player/side-action/${league.id}`)}
                    >
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-lg">🎯</span>
                          <h4 className="text-white font-semibold text-lg truncate">{league.name}</h4>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500/20 text-orange-300 border border-orange-500/30 whitespace-nowrap">
                            Side Action
                          </span>
                        </div>
                        <div className="space-y-1 text-sm text-orange-200">
                          <div>
                            <span className="font-semibold text-white">Venue:</span>
                            <span className="ml-1">{league.venue}</span>
                          </div>
                          {league.city && (
                            <div>
                              <span className="font-semibold text-white">Location:</span>
                              <span className="ml-1">{league.city}{league.country ? `, ${league.country}` : ''}</span>
                            </div>
                          )}
                          <div>
                            <span className="font-semibold text-white">Duration:</span>
                            <span className="ml-1">{new Date(league.start_date).toLocaleDateString()} – {new Date(league.end_date).toLocaleDateString()}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-white">Organizer:</span>
                            <span className="ml-1">{league.bookie_name}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid md:grid-cols-3 gap-6">
            <div
              className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition duration-300 cursor-pointer group"
              onClick={() => navigate('/player/leagues/browse')}
            >
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">🌎</div>
                <h3 className="text-xl font-bold text-white mb-2">{t('player.browseLeaguesTitle')}</h3>
                <p className="text-blue-200 text-sm">
                  {t('player.discoverLeagues')}
                </p>
              </div>
            </div>

            <div className={`bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition duration-300 ${canAccessPremiumFeatures ? 'cursor-pointer group' : 'cursor-not-allowed opacity-75'
                }`}
              onClick={() => {
                if (canAccessPremiumFeatures) {
                  navigate('/player/analytics');
                }
              }}
            >
              <div className="text-center">
                <div className={`text-4xl mb-4 ${canAccessPremiumFeatures ? 'group-hover:scale-110 transition-transform duration-200' : ''}`}>📊</div>
                <h3 className="text-xl font-bold text-white mb-2">{t('player.myStats')}</h3>
                <p className="text-blue-200 text-sm">
                  {t('player.viewStats')}
                </p>
              </div>
            </div>

            <div
              className={`bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition duration-300 ${canAccessPremiumFeatures ? 'cursor-pointer group' : 'cursor-not-allowed opacity-75'
                }`}
              onClick={() => {
                if (canAccessPremiumFeatures) {
                  navigate('/player/teams');
                }
              }}
            >
              <div className="text-center">
                <div className={`text-4xl mb-4 ${canAccessPremiumFeatures ? 'group-hover:scale-110 transition-transform duration-200' : ''}`}>👥</div>
                <h3 className="text-xl font-bold text-white mb-2">{t('player.myTeamsTitle')}</h3>
                <p className="text-blue-200 text-sm">
                  {canAccessPremiumFeatures ? t('player.manageTeams') : t('player.premiumFeature')}
                </p>
              </div>
            </div>
          </div>

          {/* Status Info */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">{t('player.accountInformation')}</h3>
              <button
                onClick={() => setShowChangePasswordModal(true)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-lg transition group"
              >
                <svg className="w-6 h-6 text-white/60 group-hover:rotate-45 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm font-semibold text-white/70 group-hover:text-white transition">Change Password</span>
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-blue-200">
              <div>
                <p><strong className="text-white">{t('auth.email')}:</strong> {user.email}</p>
                <p><strong className="text-white">{t('auth.location')}:</strong> {user.city && user.country ? `${user.city}, ${user.country}` : t('player.notSpecified')}</p>
              </div>
              <div>
                <p><strong className="text-white">{t('player.accountType')}:</strong> {t('player.player')}</p>
                <p><strong className="text-white">{t('player.memberSince')}:</strong> {t('player.recentlyJoined')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showChangePasswordModal && (
        <ChangePasswordModal accentColor="blue" onClose={() => setShowChangePasswordModal(false)} />
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-xl p-8 border border-purple-400 max-w-md w-full">
            <div className="text-center">
              <div className="text-6xl mb-4">👑</div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('player.upgradeToPremium')}</h3>
              <p className="text-purple-200 mb-6">
                {t('player.subscriptionPrice')} <span className="text-3xl font-bold text-yellow-300">$30 USD {t('player.perYear')}!</span>
              </p>
              <ul className="text-left text-purple-200 mb-6 space-y-2">
                <li className="flex items-start">
                  <span className="mr-2">🎳</span>
                  <span>{t('player.subscriptionBenefit1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">📊</span>
                  <span>{t('player.subscriptionBenefit2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">📅</span>
                  <span>{t('player.subscriptionBenefit3')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🏆</span>
                  <span>{t('player.subscriptionBenefit4')}</span>
                </li>
              </ul>
              <div className="flex space-x-4">
                <button
                  onClick={handleSubscribe}
                  disabled={isProcessingSubscription}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-yellow-900 font-bold py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessingSubscription ? t('player.processing') : t('player.subscribeNow')}
                </button>
                <button
                  onClick={() => setShowSubscriptionModal(false)}
                  disabled={isProcessingSubscription}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerDashboard;
